import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import ContentNetworkLayer from '../../helpers';
import {
  ARRANGEINORDER,
  DRAGDROP,
  FIB,
  MCQ,
  MULTISELECT,
  SWIPELEFTRIGHT,
  TRUE_FALSE,
} from '../../globals/constants';
import { useDispatch, useSelector } from 'react-redux';
import { getAnswers, setAnswers, setQuestion, getQuestion } from '../../globals/application';
import { subjectUsesMath } from '../EquationModal';
import Question from '../QuestionTypes/Question';
import {
  ArrangeInOrder,
  DragAndDrop,
  MultipleChoice,
  MultiSelect,
  SwipeLeftRight,
  TrueOrFalse,
} from '../QuestionTypes';

const TYPE_LABELS = {
  [MCQ]: 'Multiple Choice',
  [ARRANGEINORDER]: 'Arrange In Order',
  [FIB]: 'Fill In The Blanks',
  [TRUE_FALSE]: 'True / False',
  [MULTISELECT]: 'Multi-Select',
  [SWIPELEFTRIGHT]: 'Swipe Left / Right',
  [DRAGDROP]: 'Drag and Drop',
};

const HINTS = {
  [FIB]: 'Type "blank" where you want the answer inserted. e.g. "The capital of France is blank."',
  [ARRANGEINORDER]: 'Enter items in the correct order — they will be randomized for the student.',
  [SWIPELEFTRIGHT]:
    'Student will swipe the question card left or right. Customize the labels for each direction.',
  [DRAGDROP]:
    'Choose Reorder mode (arrange items in order) or Categorize mode (sort items into categories).',
};

function answersValid(questionType, answers, questionText) {
  if (!answers && answers !== false) return false;
  if (questionType === TRUE_FALSE) return answers === 'true' || answers === 'false';
  if (questionType === SWIPELEFTRIGHT) {
    return answers?.correctAnswer === 'left' || answers?.correctAnswer === 'right';
  }
  if (questionType === DRAGDROP) {
    if (!answers?.mode) return false;
    if (answers.mode === 'reorder') {
      return answers.items?.length >= 2 && answers.items.every(it => it.text?.trim());
    }
    if (answers.mode === 'categorize') {
      return (
        answers.zones?.length >= 2 &&
        answers.zones.every(z => z.label?.trim()) &&
        answers.items?.length >= 2 &&
        answers.items.every(it => it.text?.trim())
      );
    }
    return false;
  }
  if (!Array.isArray(answers) || answers.length === 0) return false;
  if (questionType === MCQ || questionType === FIB) {
    return answers.some(a => a.is_correct) && answers.some(a => a.answer?.trim());
  }
  if (questionType === MULTISELECT) {
    return answers.some(a => a.is_correct) && answers.some(a => a.answer?.trim());
  }
  if (questionType === ARRANGEINORDER) {
    return answers.length >= 2 && answers.every(a => a.ans?.trim());
  }
  return true;
}

export default function AddSubTopic() {
  const dispatch = useDispatch();
  const answers = useSelector(getAnswers);
  const question = useSelector(getQuestion);

  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subject, setSubject] = useState('');
  const [topic, setTopic] = useState('');
  const [subTopic, setSubTopic] = useState('');
  const [subTopicId, setSubTopicId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Subtopic Questions state
  const [questionType, setQuestionType] = useState('');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [questionLoading, setQuestionLoading] = useState(false);
  const [subtopicQuestions, setSubtopicQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const filteredTopics = topics.filter(t => t.code === parseInt(subject));
  const isValid = topic && subTopic.trim();

  // Check if selected subject uses math symbols
  const selectedSubject = useMemo(
    () => subjects.find(s => s.code === parseInt(subject)),
    [subjects, subject]
  );
  const showEquationButton = useMemo(
    () => subjectUsesMath(selectedSubject?.name),
    [selectedSubject]
  );

  // Clear answers + question text when type changes
  useEffect(() => {
    dispatch(setAnswers([]));
    dispatch(setQuestion(''));
  }, [questionType, dispatch]);

  const isQuestionValid =
    questionType &&
    question.trim() &&
    answersValid(questionType, answers, question) &&
    (questionType !== FIB || question.toLowerCase().includes('blank'));

  const fibMissingBlank =
    questionType === FIB && question.trim() && !question.toLowerCase().includes('blank');

  useEffect(() => {
    Promise.all([ContentNetworkLayer.getSubjects(), ContentNetworkLayer.getTopics()])
      .then(([subRes, topRes]) => {
        setSubjects(subRes.subjects);
        setTopics(topRes.topics);
      })
      .catch(() => toast.error('Could not load data.'));
  }, []);

  // Load subtopic questions when subTopicId changes
  useEffect(() => {
    if (subTopicId) {
      setLoadingQuestions(true);
      ContentNetworkLayer.getSubtopicQuestions(subTopicId)
        .then(res => setSubtopicQuestions(res?.questions || []))
        .catch(() => {
          toast.error('Could not load subtopic questions.');
          setSubtopicQuestions([]);
        })
        .finally(() => setLoadingQuestions(false));
    }
  }, [subTopicId]);

  const handleFile = f => {
    setFileName(f.name);
    const reader = new FileReader();
    reader.readAsDataURL(f);
    reader.onload = () => setFile(reader.result);
    reader.onerror = () => toast.error('Could not read file.');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);
    try {
      const response = await ContentNetworkLayer.createSubTopic({
        topic_id: parseInt(topic),
        title: subTopic.trim(),
        code: parseInt(subject),
      });
      toast.success('Sub-topic added successfully!');

      // Set subtopic ID from response to enable subtopic questions section
      if (response.subTopicId) {
        setSubTopicId(response.subTopicId);
      }

      // Keep subject and topic selected for adding questions
      setSubTopic('');
    } catch (error) {
      toast.error('Failed to add sub-topic. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubtopicQuestion = async e => {
    e.preventDefault();
    if (!isQuestionValid || !subTopicId) return;
    setQuestionLoading(true);

    const question_id = Math.floor(100000 + Math.random() * 900000);

    const payload = {
      question: question,
      question_id,
      question_type: questionType,
      subtopic_id: subTopicId,
    };

    // True/False: backend reads `answer` field, not `answers`
    if (questionType === TRUE_FALSE) {
      payload.answer = answers;
    } else if (questionType === SWIPELEFTRIGHT) {
      payload.answer = answers?.correctAnswer;
      payload.left_label = answers?.leftLabel || 'Incorrect';
      payload.right_label = answers?.rightLabel || 'Correct';
    } else if (questionType === DRAGDROP) {
      // Backend expects snake_case: item_text, correct_position, zone_id, zone_label
      payload.drag_drop_items = JSON.stringify(
        answers?.items?.map((item, idx) => ({
          item_text: item.text,
          correct_position: idx + 1,
          zone_id: item.zoneId || null,
          zone_label: answers?.zones?.find(z => z.id === item.zoneId)?.label || null,
        })) || []
      );
    } else {
      // MCQ, MultiSelect, FIB, ArrangeInOrder
      const formattedAnswers = answers.map((a, idx) => ({
        answer: a.answer || a.ans,
        ans: a.ans,
        isCorrect: a.is_correct || false,
        order: idx + 1,
      }));
      payload.answers = JSON.stringify(formattedAnswers);
    }

    if (file) {
      payload.file = file;
      payload.file_id = Math.floor(100000 + Math.random() * 900000);
      payload.filename = `subtopic-question-image-${question_id}`;
    }

    try {
      await ContentNetworkLayer.createSubtopicQuestion(payload);
      toast.success('Subtopic question added!');
      setQuestionType('');
      dispatch(setQuestion(''));
      dispatch(setAnswers([]));
      setFile(null);
      setFileName('');

      // Reload subtopic questions
      if (subTopicId) {
        const updatedQuestions = await ContentNetworkLayer.getSubtopicQuestions(subTopicId);
        setSubtopicQuestions(updatedQuestions?.questions || []);
      }
    } catch (error) {
      toast.error('Failed to add subtopic question. Please try again.');
    } finally {
      setQuestionLoading(false);
    }
  };

  const handleDeleteSubtopicQuestion = async questionId => {
    if (!window.confirm('Delete this subtopic question?')) return;

    try {
      await ContentNetworkLayer.deleteSubtopicQuestion(questionId);
      toast.success('Subtopic question deleted!');
      setSubtopicQuestions(subtopicQuestions.filter(q => q.questionId !== questionId));
    } catch (error) {
      toast.error('Failed to delete subtopic question.');
    }
  };

  return (
    <div className="form-card">
      <div className="form-card-header">
        <h2>Add Sub-Topic</h2>
        <p>Create a sub-topic under a topic and optionally add questions.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="field-group">
          <label className="field-label">Subject</label>
          <select
            className="field-select"
            value={subject}
            onChange={e => {
              setSubject(e.target.value);
              setTopic('');
              setSubTopicId(null);
            }}
          >
            <option value="">Select a subject...</option>
            {subjects.map(s => (
              <option key={s.code} value={s.code}>
                {s.name} — {s.level}
              </option>
            ))}
          </select>
        </div>

        <div className="field-group">
          <label className="field-label">Topic</label>
          <select
            className="field-select"
            value={topic}
            onChange={e => {
              setTopic(e.target.value);
              setSubTopicId(null);
            }}
            disabled={!subject}
          >
            <option value="">{subject ? 'Select a topic...' : 'Select a subject first'}</option>
            {filteredTopics.map(t => (
              <option key={t.topicId} value={t.topicId}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field-group">
          <label className="field-label">Sub-Topic Title</label>
          <input
            className="field-input"
            type="text"
            placeholder="e.g. Quadratic Equations"
            value={subTopic}
            onChange={e => setSubTopic(e.target.value)}
          />
        </div>

        <div className="form-footer">
          <span />
          <div className="form-footer-actions">
            <button type="submit" className="btn btn-primary" disabled={!isValid || loading}>
              {loading ? 'Adding...' : 'Add Sub-Topic'}
            </button>
          </div>
        </div>
      </form>

      {/* Subtopic Questions Section */}
      {subTopicId && (
        <div style={{ marginTop: '40px', paddingTop: '40px', borderTop: '1px solid #e5e7eb' }}>
          <div className="form-card-header">
            <h2>Subtopic Questions</h2>
            <p>Add quiz questions specific to this subtopic (Learn mode only).</p>
          </div>

          {/* Subtopic Questions List */}
          {loadingQuestions ? (
            <p style={{ color: '#666' }}>Loading subtopic questions...</p>
          ) : subtopicQuestions.length > 0 ? (
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                Existing Subtopic Questions ({subtopicQuestions.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {subtopicQuestions.map(q => (
                  <div
                    key={q.questionId}
                    style={{
                      padding: '12px',
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'start',
                    }}
                  >
                    <div>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: '500' }}>
                        {q.questionText}
                      </p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                        Type: {q.type}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteSubtopicQuestion(q.questionId)}
                      style={{
                        padding: '4px 8px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Add Subtopic Question Form */}
          <form onSubmit={handleCreateSubtopicQuestion}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
              Add New Subtopic Question
            </h3>

            <div className="field-row">
              <div className="field-group">
                <label className="field-label">Question Type</label>
                <select
                  className="field-select"
                  value={questionType}
                  onChange={e => setQuestionType(e.target.value)}
                >
                  <option value="">Select type...</option>
                  {Object.entries(TYPE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {HINTS[questionType] && (
              <div className="info-banner">
                <span className="info-icon">💡</span>
                <span>{HINTS[questionType]}</span>
              </div>
            )}

            {fibMissingBlank && (
              <div
                className="info-banner"
                style={{ background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}
              >
                <span className="info-icon">⚠️</span>
                <span>Add the word "blank" in your question where the answer should go.</span>
              </div>
            )}

            {questionType && <Question showEquationButton={showEquationButton} />}

            {questionType === MCQ || questionType === FIB ? (
              <MultipleChoice showEquationButton={showEquationButton} />
            ) : questionType === MULTISELECT ? (
              <MultiSelect showEquationButton={showEquationButton} />
            ) : questionType === ARRANGEINORDER ? (
              <ArrangeInOrder showEquationButton={showEquationButton} />
            ) : questionType === TRUE_FALSE ? (
              <TrueOrFalse />
            ) : questionType === SWIPELEFTRIGHT ? (
              <SwipeLeftRight />
            ) : questionType === DRAGDROP ? (
              <DragAndDrop showEquationButton={showEquationButton} />
            ) : null}

            {/* Image upload */}
            {questionType && (
              <div className="field-group" style={{ marginTop: '16px' }}>
                <label className="field-label">Question Image (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
                  style={{ fontSize: '13px' }}
                />
                {fileName && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                    Selected: {fileName}
                  </p>
                )}
              </div>
            )}

            <div className="form-footer">
              <span />
              <div className="form-footer-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!isQuestionValid || questionLoading}
                >
                  {questionLoading ? 'Adding...' : 'Add Subtopic Question'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
